import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  alarmVehicle,
  createVehicle,
  deleteVehicle,
  getLockStatuses,
  getVehicleBrands,
  getVehicleByCode,
  getVehicleById,
  getVehicleHistory,
  getVehicleStatuses,
  getVehicles,
  getVehicleTypes,
  importVehiclesV2,
  lockBattery,
  lockVehicle,
  reassignLock,
  unlockBattery,
  unlockVehicle,
  updateVehicle,
  type VehicleCreateParams,
  type VehicleHistoryParams,
  type VehicleListParams,
  type VehicleUpdate,
} from "@/apis/vehicle/vehicle-apis";
import { queryKeys } from "./keys";

export function useVehicles(params: VehicleListParams = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.vehicles.list(params),
    queryFn: () => getVehicles(params),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useVehicle(id: number | null) {
  return useQuery({
    queryKey: queryKeys.vehicles.detail(id ?? 0),
    // Query is disabled when id is null, so queryFn won't run with fallback value
    queryFn: () => getVehicleById(id ?? 0),
    enabled: Boolean(id),
  });
}

export function useVehicleByCode(code: string | null) {
  return useQuery({
    // Reusing detail key might be tricky if ID/Code collision, but safe for string vs number usually
    queryKey: queryKeys.vehicles.detail(code ?? ""),
    // Query is disabled when code is null, so queryFn won't run with fallback value
    queryFn: () => getVehicleByCode(code ?? ""),
    enabled: Boolean(code),
  });
}

export function useUpdateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: VehicleUpdate }) =>
      updateVehicle(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useVehicleStatuses() {
  return useQuery({
    queryKey: queryKeys.enums.vehicleStatuses,
    queryFn: getVehicleStatuses,
    staleTime: Infinity,
  });
}

export function useVehicleBrands() {
  return useQuery({
    queryKey: queryKeys.enums.vehicleBrands,
    queryFn: getVehicleBrands,
    staleTime: Infinity,
  });
}

export function useLockStatuses() {
  return useQuery({
    queryKey: queryKeys.enums.lockStatuses,
    queryFn: getLockStatuses,
    staleTime: Infinity,
  });
}

export function useVehicleTypes() {
  return useQuery({
    queryKey: queryKeys.enums.vehicleTypes,
    queryFn: getVehicleTypes,
    staleTime: Infinity,
  });
}

export function useLockBattery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => lockBattery(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
    },
  });
}

export function useUnlockBattery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => unlockBattery(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
    },
  });
}

export function useLockVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => lockVehicle(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.map.all });
    },
  });
}

export function useUnlockVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => unlockVehicle(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.map.all });
    },
  });
}

export function useAlarmVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => alarmVehicle(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(id),
      });
    },
  });
}

export function useDeleteVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteVehicle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useImportVehicles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cityId,
      stationCode,
      file,
    }: {
      cityId: number;
      stationCode: number | null;
      file: File;
    }) => importVehiclesV2(cityId, stationCode, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useCreateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: VehicleCreateParams) => createVehicle(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useReassignLock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      vehicleId,
      lockId,
    }: {
      vehicleId: number;
      lockId: number | null;
    }) => reassignLock(vehicleId, lockId),
    onSuccess: (_data, { vehicleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(vehicleId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useVehicleHistory(params: VehicleHistoryParams | null) {
  return useQuery({
    queryKey: params
      ? queryKeys.vehicles.history(
          params.vehicleCode,
          params.startDate,
          params.endDate,
          params.page
        )
      : ["vehicles", "history", "disabled"],
    // Query is disabled when params is null, so queryFn won't run
    queryFn: () => {
      if (!params) throw new Error("VehicleHistoryParams is required");
      return getVehicleHistory(params);
    },
    enabled: Boolean(params?.vehicleCode),
    placeholderData: keepPreviousData,
  });
}
